import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from '../service/auth/Auth.service';
import { RoleEnum } from '../modules/enums/roleEnum';
import { AI_ASSISTANT_ACCESS, AI_COSTS_ACCESS, AI_MODELS_ACCESS } from '../modules/ai-assistant/ai-admin.access';
import { WATHQ_COSTS_ACCESS } from '../modules/wathq/wathq-costs.models';
import { BULK_IMPORT_JOBS_ACCESS, BULK_IMPORT_SETTINGS_ACCESS } from '../modules/product-imports/bulk-import.access';
import { PAYMENT_SETTINGS_ACCESS } from '../modules/payment-settings/payment-settings.models';

const PERMISSION_ROUTES: { group: string; route: string }[] = [
  { group: 'dashboard',     route: '/dashboard' },
  { group: 'orders',        route: '/orders' },
  { group: 'users',         route: '/users' },
  { group: 'products',      route: '/products' },
  { group: 'requestorders', route: '/request-order' },
  { group: 'suppliers',     route: '/suppliers' },
  { group: 'buyers',        route: '/buyers' },
  { group: 'reports',       route: '/reports' },
  { group: 'settlements',   route: '/settlement' },
  { group: 'ads',           route: '/ads' },
  { group: 'returns',       route: '/returns' },
  { group: 'certificates',  route: '/certificates' },
  { group: 'project',       route: '/project/settings' },
  // Matches the prefix of ADMIN_INVITATIONS_PERMISSION, so a SubAdmin granted only this lands here
  // rather than on /no-permissions. Inert if the real claim turns out to use another group.
  { group: 'invitations',   route: '/admin/invitations' },
];

// The bulk import, payment settings, AI and Wathq cost pages, checked after the groups: their admins hold only
// exact permissions that no group above matches. Read from the routes' constants so the
// landing cannot drift from what the guard admits. Same order as the menu.
const ACCESS_LANDINGS: { anyPermission: readonly string[]; route: string }[] = [
  { anyPermission: [BULK_IMPORT_JOBS_ACCESS.permission],     route: '/products/imports' },
  { anyPermission: [BULK_IMPORT_SETTINGS_ACCESS.permission], route: '/products/imports/settings' },
  { anyPermission: [PAYMENT_SETTINGS_ACCESS.permission], route: '/payment-settings/commission' },
  { anyPermission: AI_ASSISTANT_ACCESS.anyPermission, route: '/ai-assistant' },
  { anyPermission: [AI_COSTS_ACCESS.permission],      route: '/ai-cost-monitoring' },
  { anyPermission: [AI_MODELS_ACCESS.permission],     route: '/project/ai-models' },
  { anyPermission: [WATHQ_COSTS_ACCESS.permission],   route: '/wathq-costs' },
];

export const roleRedirectGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.isAuthenticated()) return router.parseUrl('/login');

  if (auth.getRole() === RoleEnum.SubAdmin) {
    for (const { group, route } of PERMISSION_ROUTES) {
      if (auth.hasPermissionGroup(group)) return router.parseUrl(route);
    }
    for (const { anyPermission, route } of ACCESS_LANDINGS) {
      if (auth.hasAnyAdminPermission(anyPermission)) return router.parseUrl(route);
    }
    return router.parseUrl('/no-permissions');
  }

  if (auth.getRole() === RoleEnum.SalesManager) return router.parseUrl('/sales/salesmen');
  if (auth.getRole() === RoleEnum.Salesman) return router.parseUrl('/sales/my-profile');
  // KPIs are the only surface a Care user is authorized to call.
  if (auth.getRole() === RoleEnum.CustomerService) return router.parseUrl('/sales/kpis');

  return router.parseUrl('/dashboard');
};
